import { useCallback, useEffect, useState } from 'react';

import { getMyBranding } from 'services/api/organizations';

// Fired by JWTContext.setSession on both sign-in and sign-out. ThemeCustomization
// renders above the auth provider, so an event is the only way it learns that the
// signed-in user changed.
export const AUTH_SESSION_EVENT = 'ayro:auth-session';

// Last resolved branding, so a reload paints the organization's colours on the
// first frame instead of flashing the default palette and then correcting itself.
const CACHE_KEY = 'ayro-org-branding';

const readCache = () => {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    // A corrupt cache must never stop the app from theming itself.
    return null;
  }
};

const writeCache = (value) => {
  try {
    if (value) localStorage.setItem(CACHE_KEY, JSON.stringify(value));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    /* storage full or blocked — the branding just won't be cached */
  }
};

/**
 * The signed-in organization's branding, or null when the built-in theme applies.
 *
 * Deliberately standalone (no react-query, no useAuth): this runs above both of
 * those providers. Returns { palette, components, font_family } exactly as the
 * Advanced Settings screen stores it.
 */
export default function useOrgBranding() {
  const [branding, setBranding] = useState(() => readCache());

  const refresh = useCallback(async () => {
    if (!localStorage.getItem('serviceToken')) {
      // Signed out: drop straight back to the default theme so the next user
      // does not inherit the previous one's colours on the login screen.
      setBranding(null);
      writeCache(null);
      return;
    }
    try {
      const res = await getMyBranding();
      const next = res?.branding || null;
      setBranding(next);
      writeCache(next);
    } catch {
      // Never let a branding lookup break the app shell — an expired token or a
      // down backend simply leaves the current (or default) theme in place.
    }
  }, []);

  useEffect(() => {
    refresh();
    const onSession = () => refresh();
    window.addEventListener(AUTH_SESSION_EVENT, onSession);
    // Cross-tab sign-in/out writes localStorage, which fires `storage` in the
    // other tabs; without this they would keep the previous user's colours.
    window.addEventListener('storage', onSession);
    return () => {
      window.removeEventListener(AUTH_SESSION_EVENT, onSession);
      window.removeEventListener('storage', onSession);
    };
  }, [refresh]);

  return branding;
}
